import type { Question } from "../../types.js";

/** Practice items only. Not EPSO’s bank. Law stems follow 02024R1689-20260727. */
type Seed = Omit<Question, "source">;

const raw: readonly Seed[] = [
  {
    id: "e800",
    rating: 1080,
    conceptId: "enterprise_ai_architecture",
    text: "An institution needs current internal documents in answers and will not change model weights. The fitting pattern is…",
    correctIndex: 0,
    options: [
      "Retrieval-augmented generation over a managed corpus",
      "Continued pre-training of the foundation model",
      "A larger context window with no retrieval",
      "Reinforcement learning from human feedback alone",
    ],
  },
  {
    id: "e801",
    rating: 1120,
    conceptId: "enterprise_ai_architecture",
    text: "In enterprise AI design, “architecture” in an AD 8 ICT notice most closely means…",
    correctIndex: 2,
    options: [
      "A mandatory TOGAF certified role on every team",
      "The brand name of the cloud region",
      "End-to-end system design: patterns, data, security, build/buy",
      "Only the neural-network layer diagram",
    ],
  },
  {
    id: "e802",
    rating: 1160,
    conceptId: "enterprise_ai_architecture",
    text: "A team must serve predictions within seconds of an event, not overnight. The serving pattern is…",
    correctIndex: 1,
    options: [
      "Nightly batch scoring of a static table",
      "Online inference on a live request path",
      "Manual spreadsheet refresh by operators",
      "Retraining the model on every click",
    ],
  },
  {
    id: "e803",
    rating: 1180,
    conceptId: "enterprise_ai_architecture",
    text: "Build versus buy for a commodity language model API is mainly a question of…",
    correctIndex: 3,
    options: [
      "Which vendor SKU has the longest name",
      "Whether TOGAF forbids APIs",
      "Whether the Act bans all third-party models",
      "Control, data residency, cost, and lock-in versus time-to-value",
    ],
  },
  {
    id: "e804",
    rating: 1180,
    conceptId: "model_selection_eval",
    text: "A rare-event classifier reports 99% accuracy. The most precise concern is…",
    correctIndex: 0,
    options: [
      "Accuracy can hide a collapsed minority class",
      "Accuracy always equals legal conformity",
      "Accuracy proves there is no concept drift",
      "Accuracy replaces a fundamental-rights impact assessment",
    ],
  },
  {
    id: "e805",
    rating: 1240,
    conceptId: "model_selection_eval",
    text: "Validation metrics look excellent because future labels leaked into training features. The failure is…",
    correctIndex: 2,
    options: [
      "Underfitting on the training set",
      "A missing CE mark",
      "Train/validate leakage",
      "Covariate shift after go-live only",
    ],
  },
  {
    id: "e806",
    rating: 1280,
    conceptId: "model_selection_eval",
    text: "Training loss is low but hold-out loss is high. The most precise diagnosis is…",
    correctIndex: 1,
    options: [
      "Underfitting",
      "Overfitting",
      "The model is a GPAI model with systemic risk",
      "The deployer became the provider",
    ],
  },
  {
    id: "e807",
    rating: 1360,
    conceptId: "model_selection_eval",
    text: "Precision holds after a process change upstream, but recall collapses. The most precise diagnosis is…",
    correctIndex: 0,
    options: [
      "A shift in the positive class or its labels",
      "Train-time overfitting and nothing else",
      "A missing Article 50 watermark",
      "The context window was too small",
    ],
  },
  {
    id: "e808",
    rating: 1180,
    conceptId: "data_pipelines_quality",
    text: "Features computed one way in training and another at serve time typically cause…",
    correctIndex: 3,
    options: [
      "A prohibited practice under Article 5",
      "Automatic CE marking",
      "A notified-body designation",
      "Train/serving skew and silent quality loss",
    ],
  },
  {
    id: "e809",
    rating: 1220,
    conceptId: "data_pipelines_quality",
    text: "Data readiness for production AI is mainly about…",
    correctIndex: 1,
    options: [
      "Buying the largest GPU cluster",
      "Pipelines, quality, security, lineage, and governance",
      "Writing a social-media prompt library",
      "Replacing GDPR with a vendor DPA only",
    ],
  },
  {
    id: "e810",
    rating: 1300,
    conceptId: "data_pipelines_quality",
    text: "A quality contract for a training table should specify, at minimum…",
    correctIndex: 2,
    options: [
      "The Azure SKU of the storage account",
      "The TOGAF viewpoint number",
      "Allowed values, freshness, owners, and sensitivity",
      "The Article 99 fine percentage",
    ],
  },
  {
    id: "e811",
    rating: 1360,
    conceptId: "data_pipelines_quality",
    text: "Who should own the decision that a feature may include special-category personal data?",
    correctIndex: 0,
    options: [
      "Data protection and the system owner, with a recorded legal basis",
      "The foundation-model API vendor alone",
      "The notified body that CE-marks GPUs",
      "Anyone who can query the lake",
    ],
  },
  {
    id: "e812",
    rating: 1280,
    conceptId: "mlops_lifecycle",
    text: "A production monitor that only watches loss on the original train set will typically miss…",
    correctIndex: 1,
    options: [
      "Underfitting during the first epoch",
      "Post-deploy data or concept drift",
      "The Article 3 definition of a provider",
      "Whether MCP is a protocol",
    ],
  },
  {
    id: "e813",
    rating: 1340,
    conceptId: "mlops_lifecycle",
    text: "Input feature distributions shift; the label-generating process has not. The usual name is…",
    correctIndex: 2,
    options: [
      "Label leakage at train time",
      "Concept drift",
      "Covariate shift (data drift)",
      "A substantial modification under Article 3(23)",
    ],
  },
  {
    id: "e814",
    rating: 1420,
    conceptId: "mlops_lifecycle",
    text: "The relationship between inputs and the target changes after a policy rewrite. The usual name is…",
    correctIndex: 0,
    options: [
      "Concept drift",
      "A missing CE mark",
      "Prompt injection",
      "Train/serving skew of a hash function only",
    ],
  },
  {
    id: "e815",
    rating: 1480,
    conceptId: "mlops_lifecycle",
    text: "A safe production response to a broken new model version is usually…",
    correctIndex: 3,
    options: [
      "Delete the training data and hope",
      "Wait for a notified body to retrain it",
      "Raise everyone’s Elo rating",
      "Roll back to the last known-good artefact and investigate",
    ],
  },
  {
    id: "e816",
    rating: 1280,
    conceptId: "generative_rag",
    text: "RAG, as opposed to fine-tuning, is meant to…",
    correctIndex: 1,
    options: [
      "Update the model weights on every query",
      "Ground answers in retrieved documents without changing weights",
      "Replace human oversight under Article 14",
      "Satisfy Article 51 compute thresholds",
    ],
  },
  {
    id: "e817",
    rating: 1360,
    conceptId: "generative_rag",
    text: "A retrieved chunk contains an instruction that overrides the system policy. The failure mode is…",
    correctIndex: 0,
    options: [
      "Prompt injection via retrieved context",
      "Article 6(3) narrow procedural derogation",
      "Covariate shift of a tabular feature",
      "A missing authorised representative",
    ],
  },
  {
    id: "e818",
    rating: 1440,
    conceptId: "generative_rag",
    text: "The model cites a clause that is not in the retrieved set. The most precise term is…",
    correctIndex: 2,
    options: [
      "Covariate shift",
      "Social scoring",
      "Hallucination (ungrounded generation)",
      "A substantial modification of a high-risk system",
    ],
  },
  {
    id: "e819",
    rating: 1500,
    conceptId: "generative_rag",
    text: "Pasting a confidential memo into a shared model prompt mainly risks…",
    correctIndex: 3,
    options: [
      "A higher PageRank on the curriculum graph",
      "Automatic Art. 6(1) product classification",
      "The Board taking a vote",
      "Data leakage through the prompt or retained context",
    ],
  },
  {
    id: "e820",
    rating: 1420,
    conceptId: "agents_mcp",
    text: "In an agent stack, MCP is best described as…",
    correctIndex: 0,
    options: [
      "A protocol for exposing tools and context to a model",
      "The foundation model itself",
      "The Union market surveillance authority",
      "A vector database brand",
    ],
  },
  {
    id: "e821",
    rating: 1480,
    conceptId: "agents_mcp",
    text: "An agent that can email, pay, and write files should, by default…",
    correctIndex: 2,
    options: [
      "Run unbounded until the context window fills",
      "Treat every tool as already authorised",
      "Use scoped permissions and a human gate on irreversible acts",
      "Be classified as a notified body",
    ],
  },
  {
    id: "e822",
    rating: 1540,
    conceptId: "agents_mcp",
    text: "Human-in-the-loop on an agent is closest to which Act idea for high-risk systems?",
    correctIndex: 1,
    options: [
      "Article 51 FLOP presumption",
      "Article 14 human oversight with authority to intervene",
      "Article 50 machine-readable marking only",
      "Article 4 guaranteeing a certified literacy score",
    ],
  },
  {
    id: "e823",
    rating: 1600,
    conceptId: "agents_mcp",
    text: "A tool-using loop that retries a failed call without a stop condition mainly risks…",
    correctIndex: 3,
    options: [
      "A 6(3) preparatory-task derogation",
      "Automatic EDPS competence",
      "Presumption of conformity from a standard",
      "Error compounding and unintended side effects",
    ],
  },
  {
    id: "e824",
    rating: 1080,
    conceptId: "act_scope_roles",
    text: "Under Regulation (EU) 2024/1689, who is primarily responsible for the technical documentation of a high-risk AI system placed on the market?",
    correctIndex: 0,
    options: [
      "The provider",
      "The deployer",
      "The notified body",
      "The authorised representative of the deployer",
    ],
  },
  {
    id: "e825",
    rating: 1140,
    conceptId: "act_scope_roles",
    text: "A ministry puts its own trademark on a vendor high-risk system already on the market. Article 25’s usual effect is…",
    correctIndex: 1,
    options: [
      "The ministry stays a deployer only",
      "The ministry is considered the provider of that system",
      "The vendor becomes a notified body",
      "The AI Office automatically CE-marks it",
    ],
  },
  {
    id: "e826",
    rating: 1160,
    conceptId: "act_scope_roles",
    text: "A natural person uses a chatbot only at home for private mail. Article 2 generally…",
    correctIndex: 2,
    options: [
      "Treats them as a high-risk provider",
      "Requires a FRIA before first use",
      "Does not apply deployer duties to purely personal use",
      "Requires CE marking of the person",
    ],
  },
  {
    id: "e827",
    rating: 1200,
    conceptId: "act_scope_roles",
    text: "Free and open-source AI released on the internet is outside the Act unless it is…",
    correctIndex: 3,
    options: [
      "Written in Python",
      "Hosted in the Union",
      "Used by an SME",
      "Placed as high-risk or caught by Article 5 or 50",
    ],
  },
  {
    id: "e828",
    rating: 1180,
    conceptId: "act_prohibited",
    text: "An AI system infers pupils’ emotions in class to score “engagement”, with no medical or safety purpose. Under Article 5 this is…",
    correctIndex: 0,
    options: [
      "A prohibited practice",
      "Only an Article 50 transparency duty",
      "Always permitted if a FRIA exists",
      "A GPAI systemic-risk duty only",
    ],
  },
  {
    id: "e829",
    rating: 1260,
    conceptId: "act_prohibited",
    text: "Untargeted scraping of facial images from the open web to grow a recognition database is…",
    correctIndex: 2,
    options: [
      "A 6(3) narrow procedural task",
      "Mandatory for all GPAI providers",
      "A prohibited practice",
      "Allowed if the deployer is an SME",
    ],
  },
  {
    id: "e830",
    rating: 1340,
    conceptId: "act_prohibited",
    text: "The new Article 5 bans on non-consensual intimate material and CSAM (points (ba) and (bb)) apply from…",
    correctIndex: 1,
    options: [
      "2 February 2025",
      "2 December 2026",
      "2 August 2026 for all of Chapter II",
      "2 December 2027 with Annex III duties",
    ],
  },
  {
    id: "e831",
    rating: 1400,
    conceptId: "act_prohibited",
    text: "For those new intimate-image / CSAM rules, a deployer is prohibited when…",
    correctIndex: 3,
    options: [
      "Any synthetic image is generated, even landscapes",
      "The provider used open-source weights",
      "A FRIA was filed",
      "They use the system for the purpose of generating or manipulating that material",
    ],
  },
  {
    id: "e832",
    rating: 1200,
    conceptId: "act_high_risk",
    text: "An Annex III system only routes a form to the correct desk and does not profile anyone. The most precise Article 6(3) hook is…",
    correctIndex: 0,
    options: [
      "A narrow procedural task",
      "Always high-risk with no derogation possible",
      "A prohibited social-scoring practice",
      "A GPAI model with systemic risk",
    ],
  },
  {
    id: "e833",
    rating: 1280,
    conceptId: "act_high_risk",
    text: "The same Annex III system profiles natural persons. Article 6(3) then…",
    correctIndex: 2,
    options: [
      "Still applies if the task is preparatory",
      "Converts the system into a GPAI model",
      "Does not apply; profiling makes it high-risk",
      "Shifts all duties to the notified body",
    ],
  },
  {
    id: "e834",
    rating: 1340,
    conceptId: "act_high_risk",
    text: "As of 8 September 2026, Chapter III sections 1–3 obligations for Annex III high-risk systems…",
    correctIndex: 1,
    options: [
      "Have applied since 2 August 2026",
      "Apply from 2 December 2027",
      "Apply from 2 February 2025",
      "Were repealed by the Digital Omnibus",
    ],
  },
  {
    id: "e835",
    rating: 1400,
    conceptId: "act_high_risk",
    text: "After the Digital Omnibus, an AI feature that only optimises convenience inside a product, with no safety function, generally…",
    correctIndex: 3,
    options: [
      "Is always an Article 6(1) safety component",
      "Is a prohibited practice",
      "Must be CE-marked as high-risk by 2 August 2026",
      "Does not qualify as a safety component under Article 6(1a)",
    ],
  },
  {
    id: "e836",
    rating: 1280,
    conceptId: "act_obligations_ops",
    text: "A ministry will deploy an Annex III benefits-eligibility system. Who must perform the Article 27 FRIA before first use?",
    correctIndex: 0,
    options: [
      "The public deployer",
      "The notified body",
      "The AI Office instead of the deployer",
      "The European Artificial Intelligence Board",
    ],
  },
  {
    id: "e837",
    rating: 1360,
    conceptId: "act_obligations_ops",
    text: "When logs are under the deployer’s control, Article 26 requires keeping them for…",
    correctIndex: 2,
    options: [
      "Ten years in every case",
      "Twenty-four hours",
      "At least six months unless other law says otherwise",
      "No period; only the provider keeps logs",
    ],
  },
  {
    id: "e838",
    rating: 1420,
    conceptId: "act_obligations_ops",
    text: "Article 4 as amended requires providers and deployers to…",
    correctIndex: 1,
    options: [
      "Guarantee every staff member a certified literacy level",
      "Take measures to support AI literacy of staff and persons using systems on their behalf",
      "Sit an EPSO field-related test",
      "Appoint a notified body as literacy officer",
    ],
  },
  {
    id: "e839",
    rating: 1500,
    conceptId: "act_obligations_ops",
    text: "Human oversight under Article 26 must be assigned to people who have…",
    correctIndex: 3,
    options: [
      "A TOGAF title",
      "Shareholder status in the provider",
      "GPAI systemic-risk compute above 10^25",
      "Competence, training, authority, and support",
    ],
  },
  {
    id: "e840",
    rating: 1280,
    conceptId: "gpai_transparency",
    text: "A general-purpose AI model is presumed to have high-impact capabilities when training compute exceeds…",
    correctIndex: 0,
    options: [
      "10^25 floating-point operations",
      "10^23 floating-point operations",
      "10^26 parameters",
      "No numerical presumption exists",
    ],
  },
  {
    id: "e841",
    rating: 1360,
    conceptId: "gpai_transparency",
    text: "The public summary of content used to train a GPAI model follows a template from…",
    correctIndex: 2,
    options: [
      "The deployer",
      "The notified body",
      "The AI Office",
      "The national single point of contact only",
    ],
  },
  {
    id: "e842",
    rating: 1440,
    conceptId: "gpai_transparency",
    text: "Article 55 extra duties (eval, adversarial testing, incident reporting, cybersecurity) apply to…",
    correctIndex: 1,
    options: [
      "Every Annex III deployer",
      "Providers of GPAI models with systemic risk",
      "Personal non-professional users",
      "Notified bodies only",
    ],
  },
  {
    id: "e843",
    rating: 1500,
    conceptId: "gpai_transparency",
    text: "A deployer publishes a realistic video of a real person that was generated by AI. Article 50 generally requires…",
    correctIndex: 3,
    options: [
      "CE marking of the video file",
      "A FRIA by the AI Office",
      "Classification as high-risk under Annex I",
      "Disclosure that the content was artificially generated or manipulated",
    ],
  },
  {
    id: "e844",
    rating: 1420,
    conceptId: "trustworthiness_eval",
    text: "A public deployer already has a GDPR Article 35 DPIA. For an Article 27 FRIA they may…",
    correctIndex: 0,
    options: [
      "Cross-refer relevant DPIA parts; they cannot skip the FRIA if it applies",
      "Treat the DPIA as a full substitute in every case",
      "File the DPIA with a notified body instead",
      "Ignore both if the model is GPAI",
    ],
  },
  {
    id: "e845",
    rating: 1480,
    conceptId: "trustworthiness_eval",
    text: "Article 4a processing of special-category data for bias detection…",
    correctIndex: 2,
    options: [
      "Is mandatory for every deployer",
      "Repeals the GDPR",
      "Is exceptional, strictly necessary, and does not create a duty to do it",
      "Applies only to open-source models",
    ],
  },
  {
    id: "e846",
    rating: 1540,
    conceptId: "trustworthiness_eval",
    text: "Outputs used as evidence in an administrative file most need…",
    correctIndex: 1,
    options: [
      "A larger marketing context window",
      "Traceability, logging, and reproducibility of how the output was produced",
      "A social score of the applicant",
      "Untargeted facial scraping",
    ],
  },
  {
    id: "e847",
    rating: 1600,
    conceptId: "trustworthiness_eval",
    text: "Capability benchmarks and red-team exercises differ because red-teaming mainly…",
    correctIndex: 3,
    options: [
      "Replaces market surveillance",
      "Sets the Article 51 FLOP threshold",
      "Is the same as a FRIA template",
      "Probes misuse and failure modes, not only average scores",
    ],
  },
  {
    id: "e848",
    rating: 1420,
    conceptId: "ai_office_enforcement",
    text: "A Union institution deploys a high-risk system for its own use. The competent authority is…",
    correctIndex: 0,
    options: [
      "The European Data Protection Supervisor",
      "A notified body in the Member State of the vendor",
      "The Chair of the Board acting alone",
      "The deployer of a GPAI model in a third country",
    ],
  },
  {
    id: "e849",
    rating: 1500,
    conceptId: "ai_office_enforcement",
    text: "The same undertaking develops a GPAI model and a consumer AI system (not Annex I, not listed exceptions). Exclusive competence is…",
    correctIndex: 2,
    options: [
      "Always the national MSA of the deployer",
      "The notified body that wrote Annex XI",
      "The AI Office under Article 75",
      "The scientific panel as market surveillance authority",
    ],
  },
  {
    id: "e850",
    rating: 1560,
    conceptId: "ai_office_enforcement",
    text: "A notified body is…",
    correctIndex: 1,
    options: [
      "The Commission’s AI Office under another name",
      "A conformity-assessment body designated to assess high-risk systems",
      "The single point of contact in every Member State",
      "The deployer of a VLOP",
    ],
  },
  {
    id: "e851",
    rating: 1640,
    conceptId: "ai_office_enforcement",
    text: "Non-compliance with Article 5 prohibitions can attract administrative fines of up to…",
    correctIndex: 3,
    options: [
      "€7.5 million or 1% of worldwide turnover, whichever is higher",
      "A warning with no upper bound in Article 99",
      "€15 million or 3% only",
      "€35 million or 7% of worldwide turnover, whichever is higher",
    ],
  },
  {
    id: "e852",
    rating: 1420,
    conceptId: "policy_watch_impact",
    text: "A harmonised standard cited in the Official Journal, covering a given duty, typically gives…",
    correctIndex: 0,
    options: [
      "A presumption of conformity for that duty",
      "A repeal of the AI Act",
      "CE marking by itself with no assessment",
      "Exclusive competence for the EDPS over all GPAI models",
    ],
  },
  {
    id: "e853",
    rating: 1500,
    conceptId: "policy_watch_impact",
    text: "Article 1 of the AI Act is aimed at…",
    correctIndex: 2,
    options: [
      "Replacing all sectoral product law",
      "Banning general-purpose AI in the Union",
      "Internal-market rules plus trustworthy AI, rights protection, and innovation support",
      "Creating a single national AI ministry in each State",
    ],
  },
  {
    id: "e854",
    rating: 1560,
    conceptId: "policy_watch_impact",
    text: "Technically sound enforcement of an AI rule most nearly means…",
    correctIndex: 1,
    options: [
      "Using the highest FLOP model regardless of the legal test",
      "Applying the legal test to how the system actually works",
      "Treating every chatbot as Annex I machinery",
      "Publishing the EPSO question bank",
    ],
  },
  {
    id: "e855",
    rating: 1640,
    conceptId: "policy_watch_impact",
    text: "A codes-of-practice path for GPAI (Articles 53(4) and 56) is…",
    correctIndex: 3,
    options: [
      "A substitute that repeals Articles 53 and 55",
      "Mandatory CE marking by the Board",
      "Only for deployers of high-risk Annex III systems",
      "A way to demonstrate compliance until a harmonised standard exists",
    ],
  },
];

export const EPSO_AD8_AI_SEED_QUESTIONS: readonly Question[] = raw.map((q) => ({
  ...q,
  options: q.options as [string, string, string, string],
  source: "seed",
}));
